import React, { useState } from 'react';
import { Typography, TextField, Chip, Box, Stack } from '@mui/material';
import StandardDialog from '../StandardDialog/StandardDialog';

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (category: string, comments: string) => void;
  loading?: boolean;
}

const FEEDBACK_CATEGORIES = [
  { id: 'inaccurate', label: 'Inaccurate' },
  { id: 'incomplete', label: 'Missing / Incomplete' },
  { id: 'unhelpful', label: 'Unhelpful' },
  { id: 'ui_bug', label: 'UI bug' },
];

const FeedbackModal: React.FC<FeedbackModalProps> = ({ open, onClose, onSubmit, loading = false }) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [comments, setComments] = useState<string>('');

  const handleSubmit = () => {
    if (selectedCategory) {
      onSubmit(selectedCategory, comments);
      // Reset form after submission
      setSelectedCategory('');
      setComments('');
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setSelectedCategory('');
    setComments('');
    onClose();
  };

  return (
    <StandardDialog
      open={open}
      onClose={() => handleClose()}
      title="Flag an issue"
      onSubmit={handleSubmit}
      submitText="SUBMIT"
      closeText="CLOSE"
      maxWidth="sm"
      fullWidth
      submitButtonProps={{
        disabled: !selectedCategory || loading,
        loading: loading,
        sx: {
          minWidth: '90px',
          borderRadius: '4px',
          p: '6px 16px',
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          '&:hover': {
            backgroundColor: 'primary.dark',
            color: 'primary.contrastText',
          },
          '&:disabled': {
            backgroundColor: 'action.disabled',
            color: 'text.disabled',
          },
        },
      }}
      closeButtonProps={{
        disabled: loading,
        sx: {
          p: '6px 8px',
          borderRadius: '4px',
          minWidth: '90px',
        },
      }}
      PaperProps={{
        sx: {
          width: '470px',
        },
      }}>
      <Stack spacing={3}>
        {/* Category Selection */}
        <Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {FEEDBACK_CATEGORIES.map((category) => (
              <Chip
                key={category.id}
                label={category.label}
                clickable
                size="medium"
                variant="filled"
                aria-label={`Select feedback category: ${category.label}`}
                onClick={() => {
                  setSelectedCategory(category.id);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedCategory(category.id);
                  }
                }}
                tabIndex={0}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: '100px',
                  p: '2px',
                  color: selectedCategory === category.id ? 'primary.contrastText' : 'text.primary',
                  backgroundColor: selectedCategory === category.id ? 'primary.main' : 'action.selected',
                  borderColor: selectedCategory === category.id ? 'primary.main' : 'divider',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                    borderColor: 'primary.main',
                  },
                }}
              />
            ))}
          </Stack>
        </Box>

        {/* Comments Section */}
        <Box>
          <Typography
            variant="subtitle1"
            id="feedback-comments-label"
            sx={{
              mb: 1,
              fontWeight: 400,
              lineHeight: '150%',
              letterSpacing: '0.15px',
            }}>
            Additional comments (Optional)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="A few words to fully grasp the issue and/or what could be improved."
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            variant="outlined"
            aria-labelledby="feedback-comments-label"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '4px',
              },
            }}
          />
        </Box>
      </Stack>
    </StandardDialog>
  );
};

export default FeedbackModal;
